import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useClaim } from "../../../context/ClaimContext";
import config from "../../../config/config";
import {
  FaExclamationCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowLeft,
  FaLock,
  FaRedo,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import axiosInstance from "../../../AxiosInstance";
import { useUser } from "../../../context/UserContext";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateString } from "../../../util_functions/helpers";
import { calculateHours } from "../../../util_functions/helpers";
import { RotatingLines, Hourglass } from "react-loader-spinner";

const SupportHoursClaimComponent = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { claimId } = useParams();
  const { fetchUserDetailsById, fetchClientUserCustomerId } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const {
    claimsData,
    fetchClaimById,
    fetchAllClaims,
    triggerFetch,
    fetchTimeLeftByClaimId,
  } = useClaim();
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterSupportStartDate, setFilterSupportStartDate] = useState(null);
  const [filterSupportEndDate, setFilterSupportEndDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterInvoiceSent, setFilterInvoiceSent] = useState("");
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [allClaims, setAllClaims] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [isInvoiceGenerated, setIsInvoiceGenerated] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [areAllRowsDisabled, setAreAllRowsDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterWorkerName, setFilterWorkerName] = useState(
    `${user?.firstName} ${user?.lastName}`.trim()
  );

  const [loading, setLoading] = useState(true);

  const containerRef = useRef(null);

  const baseURL = config.API_BASE_URL;

  useEffect(() => {
    setFilterWorkerName(`${user?.firstName} ${user?.lastName}`.trim());
  }, [user]);

  useEffect(() => {
    const fetchClaims = async () => {
      try {
        await fetchAllClaims();
      } catch (error) {
        console.error("Error fetching claims:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClaims();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("Tab is now visible, fetching data...");
        fetchAllClaims();
        console.log("Fetching claims...");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location, claimId, triggerFetch, fetchAllClaims]);

  useEffect(() => {
    const handlePopState = () => {
      setIsModalOpen(false);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (claimId) {
      setIsModalOpen(true);
      const selectedClaim = allClaims.find(
        (claim) => claim.claimId === claimId
      );
      setSelectedClaim(selectedClaim);
    } else {
      setIsModalOpen(false);
      setSelectedClaim(null);
    }
  }, [claimId, allClaims]);

  useEffect(() => {
    if (claimId && claimsData.length > 0) {
      const claim = claimsData.find((claim) => claim.claimId === claimId);
      if (claim) {
        setSelectedClaim(claim);
        setIsModalOpen(true);
        setIsFinalized(claim.finalized);
        if (claim.timeLeft) {
          setTimeLeft(claim.timeLeft);
        }
      }
    }
  }, [claimId, claimsData]);

  const handleViewMore = async (claimId) => {
    const claim = await fetchClaimById(claimId);
    if (!claim) {
      console.error("Failed to fetch claim.");
      return;
    }

    setSelectedClaim(claim);
    setIsModalOpen(true);
    const timeLeftData = await fetchTimeLeftByClaimId(claimId);
    console.log("View More Time Left : ", timeLeftData);
    setTimeLeft(timeLeftData);
    try {
      const customerUser = await fetchClientUserCustomerId(claim.clientId);
      console.log("Customer User Details:", customerUser);
      const customerId =
        customerUser.paymentDetails.paymentIntentDetails.customerId;
      console.log("Customer ID:", customerId);

      if (!customerId) {
        console.error("Customer ID not available");
      }
    } catch (error) {
      console.error("Error fetching client user customer ID:", error);
    }
    console.log("Selected Claim Details:", claim);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const localDate = new Date(
      date.getTime() + date.getTimezoneOffset() * 60000
    );
    return localDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const renderRates = (rate) => {
    if (!rate)
      return (
        <>
          <td>No rate value</td>
          <td>No rate type</td>
        </>
      );
    return (
      <>
        <td>
          <strong>${rate.value.toFixed(2)}</strong>
        </td>
        <td>{rate.type}</td>
      </>
    );
  };

  const handleNameFilterChange = (e) => {
    setFilterWorkerName(e.target.value);
    setIsFilterApplied(true);
  };

  const handleSupportStartDateChange = (date) => {
    setFilterSupportStartDate(date);
    setIsFilterApplied(true);
  };

  const handleSupportEndDateChange = (date) => {
    setFilterSupportEndDate(date);
    setIsFilterApplied(true);
  };

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setIsFilterApplied(true);
  };

  const handleInvoiceSentFilterChange = (e) => {
    const value =
      e.target.value === "true"
        ? true
        : e.target.value === "false"
        ? false
        : "";
    setFilterInvoiceSent(value);
    setIsFilterApplied(true);
  };

  const handleClearFilters = () => {
    setFilterWorkerName("");
    setFilterSupportStartDate(null);
    setFilterSupportEndDate(null);
    setFilterStatus("");
    setFilterInvoiceSent("");
    setIsFilterApplied(false);
    setSelectedClaims([]);
  };

  const createInvoiceObject = (customerId, customerEmail, claim) => {
    const amountInCents = Math.round(claim.totalAmountDue * 100);
    const description = `Invoice for Claim Reference# ${claim.claimId}`;
    return {
      customerId: customerId,
      accountId: claim.paymentAccount,
      amount: amountInCents,
      currency: "usd",
      description: description,
      clientName: claim.clientName,
      workerName: claim.workerName,
      claimReferenceNumber: claim.claimId,
      agreementReferenceNumber: claim.agreementId,
      invoiceDescription: description,
    };
  };

  const handleSelectAll = () => {
    setIsAllSelected((prev) => {
      const newIsAllSelected = !prev;

      if (newIsAllSelected) {
        const allSelectableClaims = filteredClaims
          .filter((claim) => {
            const isDisabled =
              (claim.status === "Approved" && claim.invoiceSent) ||
              (claim.status === "Pending" && !claim.invoiceSent);
            return !isDisabled;
          })
          .map((claim) => claim.claimId);

        setSelectedClaims(allSelectableClaims);

        const fetchAndLogInvoices = async () => {
          const invoices = await Promise.all(
            allSelectableClaims.map(async (claimId) => {
              const claim = claimsData.find(
                (claim) => claim.claimId === claimId
              );
              if (claim) {
                const customerUser = await fetchClientUserCustomerId(
                  claim.clientId
                );
                const customerId =
                  customerUser.paymentDetails.paymentIntentDetails.customerId;
                const customerEmail = customerUser.email;
                return createInvoiceObject(customerId, customerEmail, claim);
              }
              return null;
            })
          );
          console.log(
            "All Selected Invoice Objects:",
            invoices.filter((invoice) => invoice !== null)
          );
        };

        fetchAndLogInvoices();
      } else {
        setSelectedClaims([]);
        logSelectedClaims([]);
      }

      return newIsAllSelected;
    });
  };

  const handleSelectClaim = async (claimId) => {
    const claim = claimsData.find((claim) => claim.claimId === claimId);
    const isDisabled =
      (claim.status === "Approved" && claim.invoiceSent) ||
      (claim.status === "Approved" && !claim.finalized) ||
      (claim.status === "Pending" && !claim.invoiceSent);

    setSelectedClaims((prevSelectedClaims) => {
      const isAlreadySelected = prevSelectedClaims.includes(claimId);
      const newSelectedClaims = isAlreadySelected
        ? prevSelectedClaims.filter((id) => id !== claimId)
        : isDisabled
        ? prevSelectedClaims
        : [...prevSelectedClaims, claimId];

      const fetchAndLogInvoices = async () => {
        const invoices = await Promise.all(
          newSelectedClaims.map(async (claimId) => {
            const claim = claimsData.find((claim) => claim.claimId === claimId);
            if (claim) {
              const customerUser = await fetchClientUserCustomerId(
                claim.clientId
              );
              const customerId =
                customerUser.paymentDetails.paymentIntentDetails.customerId;
              const customerEmail = customerUser.email;
              return createInvoiceObject(customerId, customerEmail, claim);
            }
            return null;
          })
        );
        console.log(
          "Selected Invoice Objects:",
          invoices.filter((invoice) => invoice !== null)
        );
      };

      fetchAndLogInvoices();

      return newSelectedClaims;
    });
  };

  const logSelectedClaims = (
    selectedClaims,
    customerId = null,
    selectedClaim = null
  ) => {
    const selectedClaimsDetails = claimsData.filter((claim) =>
      selectedClaims.includes(claim.claimId)
    );

    if (customerId && selectedClaim) {
      const invoice = createInvoiceObject(customerId, selectedClaim);
      console.log("Invoice Object:", invoice);
    } else {
      console.log("All Selected Claims Details:", selectedClaimsDetails);
    }
  };

  const handleGenerateSelectedInvoices = async () => {
    setSuccessMessage("");
    setIsGeneratingInvoices(true);
    for (const claimId of selectedClaims) {
      const claim = claimsData.find((claim) => claim.claimId === claimId);
      if (claim) {
        console.log(`Generating invoice for: ${claimId}`);
        await generateInvoice(claim);
        setProgress(
          (prevProgress) => prevProgress + 100 / selectedClaims.length
        );
      }
    }
    setSelectedClaims([]);
  };

  const generateInvoice = async (selectedClaim) => {
    const smoothScrollToTop = () => {
      const element = containerRef.current;
      if (element) {
        const scrollDuration = 600;
        const scrollStep = -element.scrollTop / (scrollDuration / 15);
        const scrollInterval = setInterval(() => {
          if (element.scrollTop !== 0) {
            element.scrollTop += scrollStep;
          } else {
            clearInterval(scrollInterval);
          }
        }, 15);
      }
    };

    try {
      smoothScrollToTop();
      setIsGeneratingInvoices(true);
      setSuccessMessage("Fetching customer details...");
      setProgress(20);
      const customerUser = await fetchClientUserCustomerId(
        selectedClaim.clientId
      );
      const customerId =
        customerUser.paymentDetails.paymentIntentDetails.customerId;
      const customerEmail = customerUser.email;

      if (!customerId) {
        console.error("Customer ID not available");
        setIsGeneratingInvoices(false);
        return;
      }

      const amountInCents = Math.round(selectedClaim.totalAmountDue * 100);
      setSuccessMessage("Creating invoice...");
      setProgress(40);

      const description = `Invoice for Claim Reference# ${selectedClaim.claimId}`;

      const createResponse = await axiosInstance.post(
        `${baseURL}/v1/stripe/create-invoice`,
        {
          customerId: customerId,
          accountId: selectedClaim.paymentAccount,
          amount: amountInCents,
          currency: "usd",
          description: description,
          clientName: selectedClaim.clientName,
          workerName: selectedClaim.workerName,
          claimReferenceNumber: selectedClaim.claimId,
          agreementReferenceNumber: selectedClaim.agreementId,
          invoiceDescription: description,
        }
      );

      setSuccessMessage("Invoice created. Finalizing...");
      setProgress(60);

      if (createResponse.data && createResponse.data.success) {
        const invoiceId = createResponse.data.invoiceId;

        const finalizeResponse = await axiosInstance.post(
          `${baseURL}/v1/stripe/finalize-invoice`,
          {
            invoiceId: invoiceId,
          }
        );

        setSuccessMessage("Finalizing invoice...");
        setProgress(80);

        if (finalizeResponse.data && finalizeResponse.data.success) {
          try {
            const finalizedInvoiceData = finalizeResponse.data;
            const invoiceDetails = {
              success: finalizedInvoiceData.success,
              invoiceId: invoiceId,
              claimReferenceNumber: selectedClaim.claimId,
              agreementReferenceNumber: selectedClaim.agreementId,
              created: finalizedInvoiceData.created,
              customerId: customerId,
              customerEmail: customerEmail,
              amountDue: amountInCents,
              clientName: selectedClaim.clientName,
              workerName: selectedClaim.workerName,
              currency: finalizedInvoiceData.currency,
              status: finalizedInvoiceData.status,
              finalizedAt: finalizedInvoiceData.finalizedAt,
              paidAt: finalizedInvoiceData.paidAt,
              hostedInvoiceUrl: finalizedInvoiceData.hostedInvoiceUrl,
              paymentStatus: finalizedInvoiceData.paymentStatus,
              description: `Invoice for Claim Reference# ${selectedClaim.claimId}`,
              invoiceSent: false,
              invoicePaid: false,
            };

            const saveInvoiceResponse = await axiosInstance.post(
              `${baseURL}/v1/invoices`,
              invoiceDetails
            );

            setSuccessMessage("Saving invoice...");
            setProgress(90);

            if (saveInvoiceResponse.data && saveInvoiceResponse.data.success) {
              setIsInvoiceGenerated(true);
              setSuccessMessage("Invoice successfully generated.");
              console.log(
                "Invoice saved successfully:",
                saveInvoiceResponse.data
              );

              const patchInvoiceResponse = await axiosInstance.patch(
                `${baseURL}/v1/invoices/${invoiceId}`,
                { caseType: "invoice_sent" }
              );

              if (
                patchInvoiceResponse.data &&
                patchInvoiceResponse.data.success
              ) {
                console.log("Invoice marked as sent successfully");

                await axiosInstance.patch(
                  `${baseURL}/v1/claims/${selectedClaim.claimId}`,
                  {
                    caseType: "generate_invoice",
                  }
                );
              } else {
                console.error(
                  "Failed to mark invoice as sent:",
                  patchInvoiceResponse.data
                );
              }
            } else {
              console.error(
                "Failed to save invoice:",
                saveInvoiceResponse.data
              );
            }
          } catch (saveError) {
            console.error("Error during saving invoice:", saveError);
            if (saveError.response) {
              console.error(
                "Save Invoice Response Error Data:",
                saveError.response.data
              );
              console.error(
                "Save Invoice Response Status:",
                saveError.response.status
              );
              console.error(
                "Save Invoice Response Headers:",
                saveError.response.headers
              );
            }
          }
        } else {
          console.error("Failed to finalize invoice:", finalizeResponse.data);
        }
      } else {
        console.error("Failed to create invoice:", createResponse.data);
      }
      setProgress(100);
    } catch (error) {
      console.error("Error during invoice creation or finalization:", error);
    } finally {
      setTimeout(() => {
        setProgress(0);
        setIsGeneratingInvoices(false);
      }, 1500);
    }
  };

  const filteredClaims = claimsData.filter((claim) => {
    const nameFilter = filterWorkerName.toLowerCase();
    const matchesWorkerName = nameFilter
      ? claim.workerName.toLowerCase().includes(nameFilter)
      : true;
    const matchesClientName = nameFilter
      ? claim.clientName.toLowerCase().includes(nameFilter)
      : true;

    const matchesSupportDate =
      filterSupportStartDate || filterSupportEndDate
        ? moment(claim.startDate).isBetween(
            filterSupportStartDate,
            filterSupportEndDate,
            undefined,
            "[]"
          )
        : true;

    const matchesStatus = filterStatus ? claim.status === filterStatus : true;

    const matchesInvoiceSent =
      filterInvoiceSent !== "" ? claim.invoiceSent === filterInvoiceSent : true;

    return (
      (matchesWorkerName || matchesClientName) &&
      matchesSupportDate &&
      matchesStatus &&
      matchesInvoiceSent
    );
  });

  const lastItemIndex = currentPage * itemsPerPage;
  const firstItemIndex = lastItemIndex - itemsPerPage;
  const currentItems = filteredClaims.slice(firstItemIndex, lastItemIndex);
  const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);

  useEffect(() => {
    const allDisabled = filteredClaims.every((claim) => {
      const isDisabled =
        (claim.status === "Approved" && claim.invoiceSent) ||
        (claim.status === "Approved" && !claim.finalized) ||
        (claim.status === "Pending" && !claim.invoiceSent);
      return isDisabled;
    });
    setAreAllRowsDisabled(allDisabled);
  }, [filteredClaims]);

  const handleOpenForEditing = async (claim) => {
    try {
      const response = await axiosInstance.patch(
        `${config.API_BASE_URL}/v1/claims/${claim.claimId}`,
        { caseType: "open_for_editing" }
      );
      if (response.data) {
        setSelectedClaim((prevClaim) => ({
          ...prevClaim,
          isAllowedEdit: true,
        }));
        fetchAllClaims();
      } else {
        console.error("Failed to set claim as editable:", response);
      }
    } catch (error) {
      console.error("Error setting claim to editable:", error);
    }
  };

  const handleClose = () => {
    setSuccessMessage("");
  };

  const handleBackClick = () => {
    setIsModalOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="adminsupportclaim-support-container-admin"
    >
      {successMessage && (
        <div className="claim-success-message">
          <FaCheckCircle className="invoice-check-icon" />
          <h4>{successMessage}</h4>
          <IoMdClose className="success-message-close" onClick={handleClose} />
        </div>
      )}
      {(isGeneratingInvoices || progress > 0) && (
        <div className="invoice-success-progress-bar">
          <div
            className="invoice-success-progress-bar-inner"
            style={{ width: `${progress}%` }}
          >
            {progress.toFixed(0)}%
          </div>
        </div>
      )}
      <div className="supportclaim-header"></div>
      {!isModalOpen && (
        <div className="invoice-filter-container">
          <div className="invoice-filter-itemwn">
            <label htmlFor="worker-name-filter">
              Filter by worker or client name
            </label>
            <input
              id="worker-name-filter"
              type="text"
              placeholder="Worker name ..."
              value={filterWorkerName}
              onChange={handleNameFilterChange}
            />
          </div>
          <div className="invoice-filter-itemds">
            <label>Filter by support date range</label>
            <div className="invoice-date-selects">
              <DatePicker
                selected={filterSupportStartDate}
                onChange={handleSupportStartDateChange}
                selectsStart
                startDate={filterSupportStartDate}
                endDate={filterSupportEndDate}
                placeholderText="Start date"
              />
              <DatePicker
                selected={filterSupportEndDate}
                onChange={handleSupportEndDateChange}
                selectsEnd
                startDate={filterSupportStartDate}
                endDate={filterSupportEndDate}
                placeholderText="End date"
                minDate={filterSupportStartDate}
              />
            </div>
          </div>
          <div className="invoice-filter-itemst">
            <label htmlFor="status-filter">Filter by status</label>
            <select
              id="status-filter"
              value={filterStatus}
              onChange={handleStatusFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div className="invoice-filter-itemis">
            <label>Billed</label>
            <div className="invoice-itemis-radio-container">
              <label>
                <input
                  type="radio"
                  name="invoiceSent"
                  value="true"
                  checked={filterInvoiceSent === true}
                  onChange={handleInvoiceSentFilterChange}
                />
                <span>Yes</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="invoiceSent"
                  value="false"
                  checked={filterInvoiceSent === false}
                  onChange={handleInvoiceSentFilterChange}
                />
                <span>No</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="invoiceSent"
                  value=""
                  checked={filterInvoiceSent === ""}
                  onChange={handleInvoiceSentFilterChange}
                />
                <span>All</span>
              </label>
            </div>
          </div>
          <div className="clear-filters-container">
            <button
              className="clear-filters-button"
              onClick={handleClearFilters}
            >
              <FaRedo className="invoice-filter-reset" /> Reset
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="loading-spinner">
          <RotatingLines
            visible={true}
            height="96"
            width="56"
            color="grey"
            strokeWidth="3"
            animationDuration="0.75"
            ariaLabel="rotating-lines-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
        </div>
      ) : filteredClaims.length === 0 ? (
        <div className="no-claims-message">
          <p>No matching claims found ...</p>
        </div>
      ) : (
        <>
          <table
            className={`supportclaim-support-table ${
              isModalOpen ? "hidden" : ""
            }`}
          >
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={areAllRowsDisabled}
                  />
                </th>
                <th>Worker</th>
                <th>Support date</th>
                <th>Session time</th>
                <th>Hours</th>
                <th>Rate/Hr</th>
                <th>Schedule</th>
                <th>Amount</th>
                <th>Claim Status</th>
                <th>Billed</th>
                <th>View More</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => {
                const hours = calculateHours(item.startTime, item.endTime);
                const amountDue = item.rate ? item.rate.value * hours : 0;

                const isDisabled =
                  (item.status === "Approved" && item.invoiceSent) ||
                  (item.status === "Approved" && !item.finalized) ||
                  (item.status === "Pending" && !item.invoiceSent);

                return (
                  <tr key={item.id || index}>
                    <td>
                      <input
                        id={`checkbox-${item.claimId}`}
                        type="checkbox"
                        checked={selectedClaims.includes(item.claimId)}
                        onChange={() => handleSelectClaim(item.claimId)}
                        disabled={isDisabled}
                      />
                    </td>
                    <td className="client-name-cell">{item.workerName}</td>
                    <td>{formatDate(item.startDate)}</td>
                    <td>{`${item.startTime} - ${item.endTime}`}</td>
                    <td>{hours}</td>
                    {renderRates(item.rate)}
                    <td>
                      <strong>${amountDue.toFixed(2)}</strong>
                    </td>
                    <td>
                      {item.status}
                      {item.status === "Pending" ? (
                        <FaExclamationCircle className="claim-status-pending" />
                      ) : item.status === "Rejected" ? (
                        <FaTimesCircle className="claim-status-rejected" />
                      ) : (
                        <FaCheckCircle className="claim-status-completed" />
                      )}
                    </td>
                    <td className="invoice-sent-style">
                      <strong
                        className={item.invoiceSent ? "yes-text" : "no-text"}
                      >
                        {item.invoiceSent ? "Yes" : "No"}
                      </strong>
                    </td>
                    <td className="invoice-sent-style">
                      <button
                        className="view-more-button"
                        onClick={() => handleViewMore(item.claimId)}
                      >
                        View more
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!isModalOpen && selectedClaims.length > 0 && (
            <div className="generate-selected-invoices-container">
              <button
                className="generate-selected-invoices-button"
                onClick={handleGenerateSelectedInvoices}
                disabled={selectedClaims.some((claimId) => {
                  const claim = claimsData.find(
                    (claim) => claim.claimId === claimId
                  );
                  return claim.invoiceSent && claim.finalized;
                })}
              >
                Generate Invoices for Selected
              </button>
              {selectedClaims.some((claimId) => {
                const claim = claimsData.find(
                  (claim) => claim.claimId === claimId
                );
                return claim.invoiceSent && claim.finalized;
              }) && (
                <p>Only unbilled and unfinalized claims can be invoiced.</p>
              )}
            </div>
          )}

          {isModalOpen && selectedClaim && (
            <div className="claim-details open">
              <div
                className="claim-back-containeradmn"
                onClick={handleBackClick}
              >
                <FaArrowLeft className="claim-back-icon" /> Back to Claims List
              </div>
              {selectedClaim.status === "Approved" && (
                <div className="claim-time-left">
                  {selectedClaim.finalized ? (
                    <div>
                      <FaLock className="claim-lock-icon" /> Worksheet has been
                      locked
                    </div>
                  ) : (
                    <>
                      Worksheet will be locked for invoicing in{" "}
                      <strong>
                        {timeLeft !== null ? (
                          timeLeft
                        ) : (
                          <Hourglass
                            visible={true}
                            height="20"
                            width="15"
                            ariaLabel="hourglass-loading"
                            wrapperStyle={{
                              display: "inline-block",
                              verticalAlign: "middle",
                              marginTop: "-5px",
                            }}
                            wrapperClass=""
                            colors={["#d4aa85", "#b81717"]}
                          />
                        )}
                      </strong>
                    </>
                  )}
                </div>
              )}

              <table>
                <tbody>
                  <tr>
                    <td>
                      <strong>Reference Number</strong>
                    </td>
                    <td>
                      <strong>{selectedClaim.claimId}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Agreement Reference Number</strong>
                    </td>
                    <td>
                      <a
                        href={`/account/admin-dashboard/agreements/${selectedClaim.agreementId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="agreement-link"
                      >
                        {selectedClaim.agreementId}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Claim Submission Date</strong>
                    </td>
                    <td>
                      {formatDateString(selectedClaim.claimSubmissionDate)}
                    </td>
                  </tr>
                  {selectedClaim.claimApprovalDate > 0 && (
                    <tr>
                      <td>
                        <strong>Approval Date</strong>
                      </td>
                      <td>
                        {formatDateString(selectedClaim.claimApprovalDate)}
                      </td>
                    </tr>
                  )}
                  {selectedClaim.editCount > 0 && (
                    <>
                      <tr>
                        <td>
                          <strong>Edited</strong>
                        </td>
                        <td>Yes</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Edit Count</strong>
                        </td>
                        <td>{selectedClaim.editCount}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Last Edited Date</strong>
                        </td>
                        <td>
                          {selectedClaim.lastEditedDate
                            ? formatDate(selectedClaim.lastEditedDate)
                            : "Not Edited"}
                        </td>
                      </tr>
                    </>
                  )}
                  {selectedClaim.finalized && (
                    <tr>
                      <td>
                        <strong>Claim Finalized (Locked) On</strong>
                      </td>
                      <td>{formatDateString(selectedClaim.finalizedDate)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <strong>Client Name</strong>
                    </td>
                    <td>
                      <strong>{selectedClaim.clientName}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Support Date</strong>
                    </td>
                    <td>{formatDate(selectedClaim.startDate)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Worker Name</strong>
                    </td>
                    <td>
                      <strong>{selectedClaim.workerName}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Start Time</strong>
                    </td>
                    <td>{selectedClaim.startTime}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>End Time</strong>
                    </td>
                    <td>{selectedClaim.endTime}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Hours</strong>
                    </td>
                    <td>
                      {calculateHours(
                        selectedClaim.startTime,
                        selectedClaim.endTime
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Rate / Hour</strong>
                    </td>
                    <td>
                      {selectedClaim.rate ? (
                        <>
                          <strong>
                            ${selectedClaim.rate.value.toFixed(2)}
                          </strong>{" "}
                          ({selectedClaim.rate.type})
                        </>
                      ) : (
                        "No rate"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Claim Status</strong>
                    </td>
                    <td>
                      {selectedClaim.status}
                      {selectedClaim.status === "Pending" ? (
                        <FaExclamationCircle className="claim-status-pending" />
                      ) : selectedClaim.status === "Rejected" ? (
                        <FaTimesCircle className="claim-status-rejected" />
                      ) : (
                        <FaCheckCircle className="claim-status-completed" />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Receiver Account</strong>
                    </td>
                    <td>
                      <strong>{selectedClaim.paymentAccount}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Invoice Sent</strong>
                    </td>
                    <td className="invoice-sent-style">
                      <strong
                        className={
                          selectedClaim.invoiceSent ? "yes-text" : "no-text"
                        }
                      >
                        {selectedClaim.invoiceSent ? "Yes" : "No"}
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Invoice ID</strong>
                    </td>
                    <td>
                      <strong>{selectedClaim.invoiceId}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Invoice Paid</strong>
                    </td>
                    <td className="invoice-sent-style">
                      <strong
                        className={
                          selectedClaim.invoicePaid ? "yes-text" : "no-text"
                        }
                      >
                        {selectedClaim.invoicePaid ? "Yes" : "No"}
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Payment ID</strong>
                    </td>
                    <td>
                      <strong>{selectedClaim.paymentId}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Session Amount</strong>
                    </td>
                    <td>
                      <strong>
                        {selectedClaim.rate
                          ? `$${(
                              selectedClaim.rate.value *
                              calculateHours(
                                selectedClaim.startTime,
                                selectedClaim.endTime
                              )
                            ).toFixed(2)}`
                          : "$0.00"}
                      </strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Meet and Greet Session Rate</strong>
                    </td>
                    <td>${selectedClaim.initialGreetingSessionRate}</td>
                  </tr>
                  <tr>
                    <td>
                      <div className="claim-total-amount-text">
                        <strong>Total Claimed Amount</strong>
                      </div>
                    </td>
                    <td>
                      <div className="claim-total-amount">
                        {selectedClaim.rate
                          ? `$${(
                              selectedClaim.rate.value *
                                calculateHours(
                                  selectedClaim.startTime,
                                  selectedClaim.endTime
                                ) +
                              parseFloat(
                                selectedClaim.initialGreetingSessionRate
                              )
                            ).toFixed(2)}`
                          : `$${parseFloat(
                              selectedClaim.initialGreetingSessionRate
                            ).toFixed(2)}`}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="edit-button-cell">
                <span className="claim-button-container">
                  <>
                    <button
                      className="claim-approve-button"
                      onClick={() => generateInvoice(selectedClaim)}
                      disabled={
                        selectedClaim.status === "Pending" ||
                        selectedClaim.status === "Rejected" ||
                        !selectedClaim.finalized ||
                        selectedClaim.invoiceSent
                      }
                    >
                      Generate Invoice
                    </button>
                    <button
                      className="claim-allow-button"
                      onClick={() => handleOpenForEditing(selectedClaim)}
                      disabled={
                        selectedClaim.status !== "Approved" ||
                        selectedClaim.isAllowedEdit ||
                        !selectedClaim.isFinalized
                      }
                    >
                      {selectedClaim.isAllowedEdit
                        ? "Editing Enabled"
                        : "Open for Editing"}
                    </button>
                  </>
                </span>
              </div>
            </div>
          )}

          {!isModalOpen && (
            <div className="supportclaim-pagination-controls">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <img src="/images/back-icon.png" alt="Previous" /> Previous
              </button>

              <span>
                {currentPage} of {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next <img src="/images/next-icon.png" alt="Next" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SupportHoursClaimComponent;
